import { useMemo, useState } from 'react';
import {
  formatDateTime,
  formatMoney,
  parseMoneyInput,
  PRODUCT_KINDS,
  returnsEmpty,
  t,
} from '@aquabox/shared';
import { useBrandMutations, useBrands, useProductMutations } from '../api/queries';
import {
  useCatalogMutations,
  useProductsOverview,
  type CatalogSource,
  type CatalogView,
  type ChannelPrice,
  type ProductWithChannels,
} from '../api/products-catalog';
import { useProductPriceSuggestions, type PriceSuggestionRow } from '../api/products-price';
import { Icon } from '../components/Icon';
import { useToast } from '../components/Toast';
import type { Brand, Product } from '../api/types';

/**
 * Depozito girişi. Boş ya da sıfır → '0' (depozitosuz iade edilebilir ürün meşrudur).
 * `parseMoneyInput` sıfırı `null` döndürür (fiyat alanı için doğru); depozitoda bu `null`
 * sunucuya gidip 400 "para biçimi" alıyordu ve 0 hiç girilemiyordu. Geçersiz metin yine
 * `null` kalır — sunucu reddeder, sessizce 0'a çevrilmez.
 */
export function depositInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '' || /^0+([.,]0{1,2})?$/.test(trimmed)) return '0';
  return parseMoneyInput(trimmed);
}

// Tek tablo grid'i: ad · satış fiyatı · depozito · Hayat · Getir · durum · eylemler
const PRODUCT_GRID_COLUMNS = '1.5fr 110px 110px 1.2fr 1.2fr 80px 190px';
// Kanal kataloğu grid'i: kanaldaki ad · fiyat · kanalda durum · bizim ürün
const CATALOG_GRID_COLUMNS = '1.5fr 110px 110px 1.8fr';

/** Boş form: yeni ürün eklerken ve düzenlemeyi bıraktıktan sonra buraya dönülür. */
const EMPTY = {
  name: '',
  kind: 'JUG_19L' as Product['kind'],
  unitPrice: '',
  depositAmount: '',
  brandId: '',
};

type Draft = typeof EMPTY;

/**
 * Ürünler (`urunler`).
 *
 * TEK TABLO, SATIR = ÜRÜN: ad · satış fiyatı · depozito · Hayat fiyatı · Getir fiyatı · durum.
 * Kanal fiyatları ürüne `config.catalogMap` ile eşlenmiş kanal katalog satırlarıdır (şema yok,
 * kanal paneline yazma yok). Pasif ürünler aynı tablonun sonunda soluk satırdır.
 *
 * Altta "Kanal kataloğu": kanal panelinin KENDİ ürün listesi (Hayat, Getir), "Kataloğu yenile"
 * ile salt okuma çekilir; her satır bir ürüne eşlenir, eşlenmemiş satır "Eşleme yok" rozeti taşır.
 * Abant panelinde ürün verisi yok, kataloğu da yok. Para hesabı yapılmaz; "farklı mı" bilgisi
 * sunucudan gelir.
 *
 * DEPOZİTO YALNIZ İADE EDİLEN ÜRÜNDE ANLAMLIDIR: koli geri gelmez, dolayısıyla depozitosu da
 * yoktur. Alan koli seçilince gizlenir.
 *
 * ÜRÜN SİLİNMEZ, PASİFLENİR (kural 1): geçmiş siparişler ve defter satırları o ürüne bağlı;
 * kaydı kaldırmak dünkü teslimatın ne olduğunu okunamaz hâle getirir.
 */
export function Products() {
  const toast = useToast();
  // Tek istek: ürün (fiyat + depozito) + eşlenmiş kanal fiyatları + kanal katalogları. Pasif
  // ürünler de gelir; aynı tabloda soluk satır olarak durur.
  const overview = useProductsOverview();
  const brands = useBrands();
  const { create, update, deactivate } = useProductMutations();
  const brandMut = useBrandMutations();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const items = overview.data?.items ?? [];
  const catalogs = overview.data?.catalogs ?? [];
  const brandList = brands.data ?? [];

  const rows = useMemo(() => activeFirst(items), [items]);

  function startCreate(): void {
    setEditing(null);
    setDraft({ ...EMPTY });
  }

  function startEdit(product: Product): void {
    setEditing(product.id);
    setDraft({
      name: product.name,
      kind: product.kind,
      unitPrice: product.unitPrice,
      depositAmount: product.depositAmount,
      brandId: product.brandId ?? '',
    });
  }

  function cancelForm(): void {
    setDraft(null);
    setEditing(null);
  }

  function submit(): void {
    if (!draft) return;
    const body = {
      name: draft.name.trim(),
      kind: draft.kind,
      unitPrice: parseMoneyInput(draft.unitPrice),
      // İade edilebilir TÜRDE (19L, 15L cam) depozito girilir; koli geri gelmez, depozitosu 0.
      // `returnsEmpty` GÖNDERİLMEZ: sunucu türden türetir (ADR 2026-09-20, products.service.ts).
      depositAmount: returnsEmpty(draft.kind) ? depositInput(draft.depositAmount) : '0',
      // Boş string → null (markasız ürün). Sunucu kabul eder.
      brandId: draft.brandId || null,
    };
    if (editing) {
      update.mutate(
        { id: editing, ...body },
        {
          onSuccess: () => {
            toast(t('prod.updated', { name: body.name }));
            cancelForm();
          },
        },
      );
    } else {
      create.mutate(body, {
        onSuccess: () => {
          toast(t('prod.created', { name: body.name }));
          setDraft(null);
        },
      });
    }
  }

  return (
    <div style={{ padding: '28px 32px 36px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--text-secondary)',
            maxWidth: 720,
            margin: 0,
          }}
        >
          {t('products.intro')}
        </p>
        <span style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={startCreate}>
          {t('prod.add')}
        </button>
      </div>

      {draft && !editing ? (
        <ProductForm
          draft={draft}
          brands={brandList}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSubmit={submit}
          pending={create.isPending}
          error={create.error}
          submitLabel={t('prod.save')}
        />
      ) : null}

      {rows.length === 0 ? (
        <p
          data-testid="prod-empty"
          style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}
        >
          {t('catalog.brandEmpty')}
        </p>
      ) : (
        <div
          className="card card-pad"
          data-testid="prod-table"
          style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
        >
          <TableHeader />
          {rows.map((p) =>
            editing === p.id && draft ? (
              <ProductForm
                key={p.id}
                draft={draft}
                brands={brandList}
                onChange={setDraft}
                onCancel={cancelForm}
                onSubmit={submit}
                pending={update.isPending}
                error={update.error}
                submitLabel={t('common.save')}
              />
            ) : (
              <ProductRow
                key={p.id}
                product={p}
                onEdit={() => startEdit(p)}
                onDeactivate={() =>
                  deactivate.mutate(p.id, {
                    onSuccess: () => toast(t('prod.deactivated', { name: p.name })),
                  })
                }
                onActivate={() =>
                  update.mutate(
                    { id: p.id, isActive: true },
                    { onSuccess: () => toast(t('catalog.activated', { name: p.name })) },
                  )
                }
                actionPending={update.isPending || deactivate.isPending}
              />
            ),
          )}
        </div>
      )}

      <ChannelCatalogSection catalogs={catalogs} products={items} />

      <BrandManager
        brands={brandList}
        createPending={brandMut.create.isPending}
        error={brandMut.create.error}
        onCreate={(v) =>
          brandMut.create.mutate(v, {
            onSuccess: (b) => toast(t('brand.created', { name: b.name })),
          })
        }
        onToggle={(b) =>
          brandMut.update.mutate(
            { id: b.id, isActive: !b.isActive },
            {
              onSuccess: () =>
                toast(
                  b.isActive
                    ? t('brand.deactivated', { name: b.name })
                    : t('brand.reactivated', { name: b.name }),
                ),
            },
          )
        }
      />

      <PriceSuggestSection
        onApply={(row, price) =>
          update.mutate(
            { id: row.productId, unitPrice: price },
            {
              onSuccess: () =>
                toast(t('priceSuggest.applied', { name: row.name, price: formatMoney(price) })),
            },
          )
        }
        applyPending={update.isPending}
      />
    </div>
  );
}

/** Aktifler önce (sunucu sırası korunur), pasifler tablonun sonunda. */
function activeFirst(items: ProductWithChannels[]): ProductWithChannels[] {
  return [...items.filter((p) => p.isActive), ...items.filter((p) => !p.isActive)];
}

// ─── tek tablo: satır = ürün ───

function TableHeader() {
  const cell = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 } as const;
  return (
    <div
      data-testid="prod-table-head"
      style={{
        display: 'grid',
        gridTemplateColumns: PRODUCT_GRID_COLUMNS,
        gap: 12,
        padding: '0 10px 8px',
      }}
    >
      <span style={cell}>{t('products.colName')}</span>
      <span style={cell}>{t('products.colPrice')}</span>
      <span style={cell}>{t('products.colDeposit')}</span>
      <span style={cell}>{t('products.colHayat')}</span>
      <span style={cell}>{t('products.colGetir')}</span>
      <span style={cell}>{t('products.colStatus')}</span>
      <span />
    </div>
  );
}

function ProductRow({
  product,
  onEdit,
  onDeactivate,
  onActivate,
  actionPending,
}: {
  product: ProductWithChannels;
  onEdit: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  actionPending: boolean;
}) {
  const muted = { fontSize: 13.5, color: 'var(--text-secondary)' } as const;
  return (
    <div
      className="rowgrid-stack"
      data-testid={`prod-row-${product.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: PRODUCT_GRID_COLUMNS,
        alignItems: 'center',
        gap: 12,
        minHeight: 58,
        padding: '6px 10px',
        borderTop: '1px solid var(--border)',
        opacity: product.isActive ? 1 : 0.55,
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontWeight: 600, fontSize: 14.5 }}>{product.name}</span>
        <BrandChip brand={product.brand} />
      </span>
      <span
        style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        data-label={t('products.colPrice')}
      >
        {formatMoney(product.unitPrice)}
        {Number(product.unitPrice) === 0 ? (
          <span
            data-testid={`prod-pending-price-${product.id}`}
            style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)' }}
          >
            {t('products.pending')}
          </span>
        ) : null}
      </span>
      <span style={{ ...muted, fontVariantNumeric: 'tabular-nums' }}>
        {product.returnsEmpty ? formatMoney(product.depositAmount) : t('prod.noDeposit')}
      </span>
      <ChannelPriceCell prices={product.channelPrices} source="HAYAT_PANEL" />
      <ChannelPriceCell prices={product.channelPrices} source="GETIR_SU" />
      <span style={muted}>
        {product.isActive ? t('products.statusActive') : t('prod.inactive')}
      </span>
      <span style={{ display: 'flex', gap: 6 }}>
        <button className="btn" style={{ height: 38 }} onClick={onEdit}>
          {t('common.edit')}
        </button>
        {product.isActive ? (
          <button className="btn" style={{ height: 38 }} onClick={onDeactivate}>
            {t('prod.deactivate')}
          </button>
        ) : (
          <button
            className="btn"
            style={{ height: 38 }}
            disabled={actionPending}
            onClick={onActivate}
            data-testid={`prod-activate-${product.id}`}
          >
            {t('catalog.activate')}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Ürüne eşlenmiş kanal fiyatı (bayi satış fiyatı; varsa tavan ve komisyon). Kanal fiyatı bizimkinden
 * farklıysa "Farklı" etiketi (karşılaştırma SUNUCUDA, Decimal ile); kanalda kapalı satır işaretlenir.
 */
function ChannelPriceCell({ prices, source }: { prices: ChannelPrice[]; source: CatalogSource }) {
  const list = prices.filter((p) => p.source === source);
  if (list.length === 0) return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {list.map((p) => (
        <span
          key={p.externalId}
          data-testid={`prod-chprice-${source}-${p.externalId}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(p.price)}
            {p.differs ? (
              <span
                data-testid={`prod-differs-${source}-${p.externalId}`}
                title={t('products.differsHint')}
                style={{ marginLeft: 6, fontSize: 11.5, color: 'var(--warning, #b45309)' }}
              >
                {t('products.differs')}
              </span>
            ) : null}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{p.name}</span>
          {p.cap || p.commission ? (
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
              {[
                p.cap ? t('products.cap', { price: formatMoney(p.cap) }) : null,
                p.commission
                  ? t('products.commission', { price: formatMoney(p.commission) })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : null}
          {p.active === false ? (
            <span style={{ fontSize: 11.5, color: 'var(--danger)' }}>
              {t('products.channelClosed')}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );
}

// ─── kanal kataloğu: kanal panelinin kendi ürün listesi (salt okunur) ───

function ChannelCatalogSection({
  catalogs,
  products,
}: {
  catalogs: CatalogView[];
  products: ProductWithChannels[];
}) {
  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <strong style={{ fontSize: 14 }}>{t('products.catalogTitle')}</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('products.catalogIntro')}
        </span>
      </div>
      {catalogs.map((c) => (
        <CatalogBlock key={c.source} catalog={c} products={products} />
      ))}
    </div>
  );
}

function CatalogBlock({
  catalog,
  products,
}: {
  catalog: CatalogView;
  products: ProductWithChannels[];
}) {
  const toast = useToast();
  const { refresh, map } = useCatalogMutations();
  const channel = t(`products.source.${catalog.source}`);
  const unmapped = catalog.items.filter((i) => i.productId === null).length;

  return (
    <div
      data-testid={`catalog-${catalog.source}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 13.5 }}>{channel}</strong>
        <span
          data-testid={`catalog-fetched-${catalog.source}`}
          style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}
        >
          {catalog.fetchedAt
            ? t('products.catalogFetchedAt', { at: formatDateTime(catalog.fetchedAt) })
            : t('products.catalogNever')}
        </span>
        <span style={{ flex: 1 }} />
        <button
          className="btn"
          data-testid={`catalog-refresh-${catalog.source}`}
          disabled={refresh.isPending}
          onClick={() =>
            refresh.mutate(catalog.source, {
              onSuccess: () => toast(t('products.catalogRefreshed', { channel })),
            })
          }
        >
          {refresh.isPending ? t('products.catalogRefreshing') : t('products.catalogRefresh')}
        </button>
      </div>

      {refresh.error ? <ErrorLine error={refresh.error} /> : null}
      {map.error ? <ErrorLine error={map.error} /> : null}

      {catalog.items.length > 0 ? (
        <>
          {unmapped > 0 ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {t('products.catalogUnmapped', { n: unmapped })}
            </span>
          ) : null}
          {catalog.items.map((item) => (
            <div
              key={item.externalId}
              data-testid={`catalog-row-${catalog.source}-${item.externalId}`}
              style={{
                display: 'grid',
                gridTemplateColumns: CATALOG_GRID_COLUMNS,
                alignItems: 'center',
                gap: 12,
                minHeight: 44,
                padding: '4px 10px',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 14 }}>{item.name}</span>
              <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(item.price)}
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {item.active === undefined
                  ? '—'
                  : item.active
                    ? t('products.channelOpen')
                    : t('products.channelClosed')}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select
                  data-testid={`catalog-map-${catalog.source}-${item.externalId}`}
                  value={item.productId ?? ''}
                  disabled={map.isPending}
                  onChange={(e) =>
                    map.mutate(
                      {
                        source: catalog.source,
                        externalId: item.externalId,
                        productId: e.target.value || null,
                      },
                      { onSuccess: () => toast(t('products.mapSaved')) },
                    )
                  }
                  style={{ height: 38, flex: 1 }}
                >
                  <option value="">{t('products.mapNone')}</option>
                  {products
                    .filter((p) => p.isActive || p.id === item.productId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </select>
                {item.productId === null ? (
                  <span
                    data-testid={`catalog-unmapped-${catalog.source}-${item.externalId}`}
                    style={{
                      fontSize: 11.5,
                      color: 'var(--warning, #b45309)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('products.mapNone')}
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

// ─── marka yönetimi ───

function BrandManager({
  brands,
  createPending,
  error,
  onCreate,
  onToggle,
}: {
  brands: Brand[];
  createPending: boolean;
  error: unknown;
  onCreate: (v: { name: string; code: string }) => void;
  onToggle: (b: Brand) => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const valid = name.trim().length >= 2 && /^[A-Za-zÇĞİÖŞÜçğıöşü]{1,2}$/.test(code.trim());

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <strong style={{ fontSize: 14 }}>{t('brand.createTitle')}</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('brand.productNameUniqueHint')}
        </span>
      </div>

      <div
        className="form-grid"
        style={
          {
            '--form-cols': '1fr 140px 140px',
            '--form-gap': '10px',
            alignItems: 'end',
          } as React.CSSProperties
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('brand.nameLabel')}</span>
          <input
            data-testid="brand-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('brand.namePlaceholder')}
            style={{ height: 44 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('brand.codePlaceholder')}</span>
          <input
            data-testid="brand-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toLocaleUpperCase('tr-TR'))}
            placeholder={t('brand.codeInputPlaceholder')}
            maxLength={2}
            style={{ height: 44, textTransform: 'uppercase' }}
          />
        </label>
        <button
          className="btn btn-primary"
          disabled={!valid || createPending}
          onClick={() => {
            onCreate({ name: name.trim(), code: code.trim() });
            setName('');
            setCode('');
          }}
          data-testid="brand-save"
        >
          {t('common.save')}
        </button>
      </div>

      {error ? <ErrorLine error={error} /> : null}

      {brands.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {brands.map((b) => (
            <BrandBadge key={b.id} brand={b} onToggle={() => onToggle(b)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BrandBadge({ brand, onToggle }: { brand: Brand; onToggle: () => void }) {
  const title = t('brand.badgeTitle', { code: brand.code, name: brand.name });
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        border: '1px solid var(--border)',
        borderRadius: 999,
        opacity: brand.isActive ? 1 : 0.55,
      }}
      data-testid={`brand-badge-${brand.id}`}
    >
      <span
        aria-hidden="true"
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: 'var(--primary)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
        }}
        title={title}
      >
        {brand.code}
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 600 }}>{brand.name}</span>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`brand-toggle-${brand.id}`}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          fontSize: 12.5,
          padding: 0,
        }}
      >
        {brand.isActive ? t('prod.deactivate') : t('brand.activate')}
      </button>
    </span>
  );
}

function BrandChip({ brand }: { brand: Product['brand'] }) {
  if (!brand) {
    return (
      <span
        data-testid="brand-chip-unbranded"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11.5,
          color: 'var(--text-secondary)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--text-secondary)',
          }}
        />
        {t('brand.unbrandedBadge')}
      </span>
    );
  }
  return (
    <span
      data-testid={`brand-chip-${brand.id}`}
      title={t('brand.badgeTitle', { code: brand.code, name: brand.name })}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11.5,
        fontWeight: 600,
        color: 'var(--text-secondary)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: 'var(--primary)',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {brand.code}
      </span>
      {brand.name}
    </span>
  );
}

// ─── form ───

function ProductForm({
  draft,
  brands,
  onChange,
  onCancel,
  onSubmit,
  pending,
  error,
  submitLabel,
}: {
  draft: Draft;
  brands: Brand[];
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSubmit: () => void;
  pending: boolean;
  error: unknown;
  submitLabel: string;
}) {
  // Depozito alanı iade edilebilir her türde açılır (tür tablosundan, `kind === 'JUG_19L'` değil).
  const jug = returnsEmpty(draft.kind);
  const valid = draft.name.trim().length >= 2 && draft.unitPrice.trim() !== '' && !pending;

  return (
    <div
      className="card card-pad"
      style={{
        borderColor: 'var(--primary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        className="form-grid"
        style={
          {
            '--form-cols': '1fr 160px 140px 140px 160px',
            '--form-gap': '10px',
            alignItems: 'end',
          } as React.CSSProperties
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('prod.name')}</span>
          <input
            data-testid="prod-name"
            value={draft.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
            placeholder={t('prod.namePlaceholder')}
            style={{ height: 44 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('prod.kind')}</span>
          <select
            data-testid="prod-kind"
            value={draft.kind}
            onChange={(e) => onChange({ ...draft, kind: e.target.value as Product['kind'] })}
            style={{ height: 44 }}
          >
            {PRODUCT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`productKind.${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('prod.unitPrice')}</span>
          <input
            data-testid="prod-unit-price"
            value={draft.unitPrice}
            onChange={(e) => onChange({ ...draft, unitPrice: e.target.value })}
            inputMode="decimal"
            placeholder="120"
            style={{ height: 44, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
        {jug ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="section-label">{t('prod.deposit')}</span>
            <input
              data-testid="prod-deposit"
              value={draft.depositAmount}
              onChange={(e) => onChange({ ...draft, depositAmount: e.target.value })}
              inputMode="decimal"
              placeholder="100"
              style={{ height: 44, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        ) : (
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', paddingBottom: 12 }}>
            {t('prod.noDepositHint')}
          </span>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="section-label">{t('prod.brand')}</span>
          <select
            data-testid="prod-brand"
            value={draft.brandId}
            onChange={(e) => onChange({ ...draft, brandId: e.target.value })}
            style={{ height: 44 }}
          >
            <option value="">{t('brand.unbrandedBadge')}</option>
            {brands
              .filter((b) => b.isActive || b.id === draft.brandId)
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} · {b.name}
                  {b.isActive ? '' : t('brand.passiveSuffix')}
                </option>
              ))}
          </select>
        </label>
      </div>

      {error ? <ErrorLine error={error} /> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={!valid || pending}
          onClick={onSubmit}
          data-testid="prod-save"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const message = (error as { message?: string } | null)?.message;
  return (
    <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
      <Icon name="alert-circle" size={14} />{' '}
      {message && message.length > 0 ? message : t('error.internal')}
    </p>
  );
}

// ─── geçmişten fiyat öner ───

/**
 * "Geçmişten fiyat öner" — `GET /api/products/price-suggestions` yalnız düğmeye basınca
 * çekilir (`enabled: false` + `refetch`, `useProductPriceSuggestions`). Fiyat SUNUCUDA
 * hesaplanır; burada yalnız gösterilir. "Uygula" mevcut ürün güncelleme ucunu (`update`)
 * çağırır — etkinleştirme AYRI düğmedir (brief: "Uygula yalnız fiyatı yazar").
 */
function PriceSuggestSection({
  onApply,
  applyPending,
}: {
  onApply: (row: PriceSuggestionRow, price: string) => void;
  applyPending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const suggestions = useProductPriceSuggestions(false);

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 14 }}>{t('priceSuggest.title')}</strong>
        <span style={{ flex: 1 }} />
        <button
          className="btn"
          data-testid="price-suggest-button"
          onClick={() => {
            setOpen(true);
            void suggestions.refetch();
          }}
          disabled={suggestions.isFetching}
        >
          {t('priceSuggest.button')}
        </button>
      </div>

      {open ? (
        suggestions.isFetching ? (
          <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
            {t('priceSuggest.loading')}
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 12 }}>
                <th style={{ padding: '4px 8px' }}>{t('priceSuggest.colProduct')}</th>
                <th style={{ padding: '4px 8px' }}>{t('priceSuggest.colSuggested')}</th>
                <th style={{ padding: '4px 8px' }}>{t('priceSuggest.colChannels')}</th>
                <th style={{ padding: '4px 8px' }}>{t('priceSuggest.colSamples')}</th>
                <th style={{ padding: '4px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {(suggestions.data?.items ?? []).map((row) => (
                <tr
                  key={row.productId}
                  data-testid={`price-suggest-row-${row.productId}`}
                  style={{ borderTop: '1px solid var(--border)' }}
                >
                  <td style={{ padding: '8px' }}>{row.name}</td>
                  <td style={{ padding: '8px', fontVariantNumeric: 'tabular-nums' }}>
                    {row.suggested ? formatMoney(row.suggested) : '—'}
                  </td>
                  <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>
                    {row.samples === 0
                      ? t('priceSuggest.noData')
                      : row.byChannel
                          .map(
                            (c) =>
                              `${t(`orderChannel.${c.channel}`)} ${formatMoney(c.median)} (${c.samples})`,
                          )
                          .join(' · ')}
                  </td>
                  <td style={{ padding: '8px', fontVariantNumeric: 'tabular-nums' }}>
                    {row.samples}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <button
                      className="btn"
                      style={{ height: 32 }}
                      disabled={!row.suggested || applyPending}
                      onClick={() => row.suggested && onApply(row, row.suggested)}
                      data-testid={`price-suggest-apply-${row.productId}`}
                    >
                      {t('priceSuggest.apply')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </div>
  );
}
